import React from "react";
import { MddDialog, MddDialogButtons, MddCheckbox } from "../../../common";
import { IStudyProtocol, IStudyPersonnelProtocol } from "../../../../model";

interface IMddStudySiteProtocolDialogProps {
  open: boolean;
  studyProtocols: IStudyProtocol[];
  existingProtocols: IStudyPersonnelProtocol[];
  onAddProtocols: (newProtocols: IStudyPersonnelProtocol[]) => void;
  onClose: () => void;
}

export const MddStudySiteProtocolDialog = ({
  open,
  studyProtocols,
  existingProtocols,
  onAddProtocols,
  onClose
}: IMddStudySiteProtocolDialogProps): JSX.Element => {
  // Internal state for selected protocols during dialog interaction
  const [selectedProtocols, setSelectedProtocols] = React.useState<string[]>(
    []
  );

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedProtocols([]);
    }
  }, [open]);

  // Memoize the set of existing protocol IDs for efficient lookup
  const existingProtocolIds = React.useMemo(
    () => new Set(existingProtocols.map(protocol => protocol.id)),
    [existingProtocols]
  );

  const handleProtocolChange = (protocolId: string, checked: boolean) => {
    if (checked) {
      setSelectedProtocols(prev => [...prev, protocolId]);
    } else {
      setSelectedProtocols(prev => prev.filter(id => id !== protocolId));
    }
  };

  const handleAddProtocols = () => {
    // Create new protocol objects from selected protocols
    const newProtocols = selectedProtocols.map(protocolId => {
      const protocol = studyProtocols.find(p => p.ProtocolId === protocolId)!;
      return {
        id: protocolId,
        protocolName: protocol.ProtocolName,
        sponsorSiteId: ""
      };
    });

    onAddProtocols(newProtocols);
    onClose();
  };

  return (
    <MddDialog
      dialogProps={{
        open,
        maxWidth: "md",
        fullWidth: true,
        className: "mdd-study-site--protocol-dialog--wrapper"
      }}
      dialogTitleProps={{
        title: "Add Protocol Participation"
      }}
      dialogContentProps={{
        className:
          "mdd-dialog--content-100percent mdd-study-site--protocol-dialog--content"
      }}
      dialogContent={
        <div className="mdd-study-site--protocol-dialog--list">
          {studyProtocols.map(protocol => {
            const isAlreadySelected = existingProtocolIds.has(
              protocol.ProtocolId
            );

            return (
              <div
                key={protocol.ProtocolId}
                className={`mdd-study-site--protocol-dialog--item ${
                  isAlreadySelected
                    ? "mdd-study-site--protocol-dialog--item--disabled"
                    : "mdd-study-site--protocol-dialog--item--enabled"
                }`}
              >
                <MddCheckbox
                  lblText={protocol.ProtocolName}
                  chkBoxProps={{
                    id: `protocol-${protocol.ProtocolId}`,
                    checked:
                      isAlreadySelected ||
                      selectedProtocols.includes(protocol.ProtocolId),
                    value: protocol.ProtocolId,
                    disabled: isAlreadySelected,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      handleProtocolChange(
                        protocol.ProtocolId,
                        e.target.checked
                      );
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      }
      dialogActions={
        <MddDialogButtons
          saveButtonText="Add Protocol(s)"
          saveButtonProps={{
            disabled: selectedProtocols.length === 0,
            onClick: handleAddProtocols
          }}
          closeButtonProps={{
            onClick: onClose
          }}
        />
      }
      showCloseButton
      onCloseClick={onClose}
    />
  );
};
